import { useMemo, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useResourceVersion, useResourceVersions } from '@/hooks/useResourceVersions';
import { diffJsonDocuments } from '@/lib/jsonDiff';
import { useFlag } from '@/hooks/useFeatureFlags';
import { PinSetList } from './PinSetList';
import { UserName } from '@/components/ui/UserName';
import { flattenPinSet } from '@/types/versioning';
import type { ResourceVersionSummary, VersionedEntityType } from '@/types/versioning';

/**
 * Version history for one resource (task 725).
 *
 * Opened from the status indicator, this is where all the *detail* the chip
 * deliberately refuses to carry lives: which versions exist, who published each
 * one and why, what each froze, and how two of them differ.
 *
 * Two reading modes, chosen by how many versions are ticked:
 *
 *   - **One version** — the frozen document whole, plus its pin set when the
 *     resource is an endpoint (the lockfile that makes the freeze real).
 *   - **Two versions** — a JSON document diff, which falls straight out of the
 *     storage model: version documents are configuration JSON, so comparing
 *     them is a document comparison, not a text one.
 *
 * Ticking a third version replaces the older of the two selections, so the
 * panel never argues with the User about clearing a checkbox first.
 *
 * Task 1311: the list and the two reading modes live in `VersionHistoryBody`,
 * which this drawer wraps and the Advanced → Agents page renders inline (its
 * History tab and the Prompt tab's History sub tab).
 */

interface VersionHistoryPanelProps {
  open: boolean;
  onClose: () => void;
  entityType: VersionedEntityType;
  entityId: string;
  /** Resource name for the panel heading. */
  resourceName?: string;
}

const statusBadgeClasses = (status: string): string =>
  status === 'published'
    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
    : 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200';

const formatTimestamp = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

interface VersionHistoryBodyProps {
  entityType: VersionedEntityType;
  entityId: string;
  /** Resource name, for the inline heading. */
  resourceName?: string;
  /** Fetch the list. The drawer passes whether it is open; inline it is always on. */
  enabled?: boolean;
  /**
   * Render the heading ("<name> — versions" and the frozen / compare line)
   * above the list. The drawer has its own title bar and leaves it off.
   */
  showHeading?: boolean;
}

/**
 * The version list, the one-version document view and the two-version diff
 * (task 1311, split out of the drawer unchanged).
 */
export function VersionHistoryBody({
  entityType,
  entityId,
  resourceName,
  enabled = true,
  showHeading = false,
}: VersionHistoryBodyProps) {
  // The list is fetched only while the body is shown — history is detail, and
  // detail should not cost a request on every detail-page render.
  const { data: versions, isLoading, isError } = useResourceVersions(entityType, entityId, {
    enabled,
  });

  /**
   * Ticked versions, oldest selection first. At most two. Held as integer pairs
   * because that is how the service addresses a version — `1.10` is the tenth
   * minor of major 1, and a decimal string would make it the first.
   */
  const [selected, setSelected] = useState<VersionRef[]>([]);
  /**
   * The JSON diff view's own lever (task 1217). Off, one version is open at a
   * time: a second tick replaces the first, so the comparison never renders
   * and the second version is never fetched.
   */
  const diffEnabled = useFlag('innovate.fe.versioning.diff');
  const maxSelected = diffEnabled ? 2 : 1;

  const toggle = (version: VersionRef) => {
    setSelected((prev) => {
      if (prev.some((entry) => sameVersion(entry, version))) {
        return prev.filter((entry) => !sameVersion(entry, version));
      }
      // A tick past the limit drops the oldest selection rather than refusing the click.
      return prev.length < maxSelected ? [...prev, version] : [...prev.slice(1), version];
    });
  };

  const [first, second] = selected;
  const firstVersion = useResourceVersion(entityType, entityId, first);
  const secondVersion = useResourceVersion(entityType, entityId, second);

  const diff = useMemo(() => {
    if (!first || !second) return [];
    if (!firstVersion.data || !secondVersion.data) return [];
    // Diff runs oldest → newest so "before"/"after" read the way they sound,
    // regardless of the order the two were ticked in.
    const [older, newer] =
      compareVersions(first, second) <= 0
        ? [firstVersion.data, secondVersion.data]
        : [secondVersion.data, firstVersion.data];
    return diffJsonDocuments(older.document, newer.document);
  }, [first, second, firstVersion.data, secondVersion.data]);

  const singleDetail = first && !second ? firstVersion.data : null;

  return (
    <div className="space-y-4" data-testid="version-history-body">
      {showHeading && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {resourceName ? `${resourceName} — versions` : 'Versions'}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Published versions are frozen.{diffEnabled && ' Tick two to compare them.'}
          </p>
        </div>
      )}

        {isLoading && (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="version-history-loading">
            Loading version history…
          </p>
        )}

        {isError && !isLoading && (
          <p className="text-sm text-red-600 dark:text-red-400" data-testid="version-history-error">
            Version history is unavailable right now. Please try again.
          </p>
        )}

        {!isLoading && !isError && (versions?.length ?? 0) === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="version-history-empty">
            No versions yet. Publishing this resource freezes its current state as v1.0.
          </p>
        )}

        {(versions ?? []).map((version) => (
          <VersionRow
            key={version.id}
            version={version}
            checked={selected.some((entry) => sameVersion(entry, version))}
            onToggle={() => toggle({ major: version.major, minor: version.minor })}
          />
        ))}

        {singleDetail && (
          <section
            data-testid="version-document-view"
            className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
          >
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              v{singleDetail.version} document
            </h3>
            {flattenPinSet(singleDetail.pinSet).length > 0 && (
              <div className="mt-3">
                <h4 className="mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                  Pinned at publish
                </h4>
                <PinSetList
                  entries={flattenPinSet(singleDetail.pinSet)}
                  testIdPrefix="version-pin-set"
                />
              </div>
            )}
            <pre className="mt-3 max-h-80 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-800 dark:bg-gray-900 dark:text-gray-200">
              {JSON.stringify(singleDetail.document ?? {}, null, 2)}
            </pre>
          </section>
        )}

        {diffEnabled && first && second && (
          <section
            data-testid="version-diff-view"
            className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
          >
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Comparing v{label(first)} and v{label(second)}
            </h3>
            {(firstVersion.isLoading || secondVersion.isLoading) && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading both versions…</p>
            )}
            {!firstVersion.isLoading && !secondVersion.isLoading && diff.length === 0 && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" data-testid="version-diff-identical">
                These two versions are identical.
              </p>
            )}
            {diff.length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {diff.map((entry) => (
                  <li
                    key={entry.path}
                    data-testid="version-diff-entry"
                    data-kind={entry.kind}
                    className="rounded-md bg-gray-50 px-2.5 py-1.5 text-xs dark:bg-gray-900/50"
                  >
                    <span className="font-mono font-medium text-gray-800 dark:text-gray-200">
                      {entry.path}
                    </span>
                    <span className="ml-2 text-gray-500 dark:text-gray-400">{entry.kind}</span>
                    <div className="mt-1 space-y-0.5 font-mono">
                      {entry.before !== undefined && (
                        <div className="text-red-700 dark:text-red-300">- {entry.before}</div>
                      )}
                      {entry.after !== undefined && (
                        <div className="text-green-700 dark:text-green-300">+ {entry.after}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
    </div>
  );
}

export function VersionHistoryPanel({
  open,
  onClose,
  entityType,
  entityId,
  resourceName,
}: VersionHistoryPanelProps) {
  // The title line says whether two versions can be compared.
  const diffEnabled = useFlag('innovate.fe.versioning.diff');

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
      />
      <DialogPanel
        transition
        data-testid="version-history-panel"
        className="fixed inset-y-0 right-0 flex w-full max-w-2xl flex-col bg-white shadow-xl transition duration-300 ease-in-out data-[closed]:translate-x-full dark:bg-gray-800"
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div>
            <DialogTitle className="text-sm font-medium text-gray-900 dark:text-white">
              Version history{resourceName ? ` — ${resourceName}` : ''}
            </DialogTitle>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Published versions are frozen.{diffEnabled && ' Tick two to compare them.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="version-history-close"
            aria-label="Close version history"
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <VersionHistoryBody
            entityType={entityType}
            entityId={entityId}
            resourceName={resourceName}
            enabled={open}
          />
        </div>
      </DialogPanel>
    </Dialog>
  );
}

interface VersionRowProps {
  version: ResourceVersionSummary;
  checked: boolean;
  onToggle: () => void;
}

function VersionRow({ version, checked, onToggle }: VersionRowProps) {
  return (
    <div
      data-testid="version-history-row"
      className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          data-testid={`version-select-${version.version}`}
          aria-label={`Select version ${version.version}`}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white">
              v{version.version}
            </span>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClasses(version.status)}`}
            >
              {version.status === 'published' ? 'Published' : 'Draft'}
            </span>
          </div>

          <dl className="mt-1.5 grid grid-cols-[6rem_1fr] gap-x-3 gap-y-0.5 text-xs text-gray-600 dark:text-gray-400">
            <dt>Published</dt>
            <dd>{formatTimestamp(version.publishedAt)}</dd>
            <dt>Author</dt>
            <dd>{version.publishedBy ? <UserName userId={version.publishedBy} /> : '—'}</dd>
            {version.notes && (
              <>
                <dt>Notes</dt>
                <dd className="whitespace-pre-wrap">{version.notes}</dd>
              </>
            )}
          </dl>

        </div>
      </div>
    </div>
  );
}

/** A version addressed the way the service addresses it. */
interface VersionRef {
  major: number;
  minor: number;
}

const sameVersion = (a: VersionRef, b: VersionRef): boolean =>
  a.major === b.major && a.minor === b.minor;

/** Compare as an integer pair, never as a decimal. */
const compareVersions = (a: VersionRef, b: VersionRef): number =>
  a.major !== b.major ? a.major - b.major : a.minor - b.minor;

const label = (version: VersionRef): string => `${version.major}.${version.minor}`;

export default VersionHistoryPanel;
